import { Link } from "react-router-dom";
import { ScrollSection } from "@/components/main-page/ScrollSection";

function IconLinkedIn() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function IconX() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function IconGitHub() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  );
}

const TEAM = [
  {
    name: "Ben Zhang",
    role: "Technical",
    bio: "15+ years in software. $100M+ in value created across FCA-regulated fintech. Founded and exited a fintech agency.",
    photo: "/team-ben.jpg",
    photoPosition: "center top",
    links: {
      linkedin: "https://www.linkedin.com/in/benwk/",
      x: "https://x.com/benwkz",
      github: "https://github.com/benwk",
    },
  },
  {
    name: "Hugh Carnegie",
    role: "Operations",
    bio: "3 products to market. $5M ARR. 5 years leading operations at early-stage companies.",
    photo: "/team-hugh.jpg",
    photoPosition: "center 15%",
    links: {
      linkedin: "https://www.linkedin.com/in/hugh-carnegie-868962b1/",
      x: "https://x.com/CarnegieHu45819",
    },
  },
  {
    name: "George Yang",
    role: "Strategic advisor",
    bio: "Investor in Anthropic, Stripe, xAI, Databricks, Notion.AI. $10B AUM. $20B+ transaction experience.",
    photo: "/team-george.png",
    photoPosition: "center 35%",
    links: {
      linkedin: "https://www.linkedin.com/in/george-y-7a96a76/",
    },
  },
];

const ICON_MAP: Record<string, () => React.JSX.Element> = {
  linkedin: IconLinkedIn,
  x: IconX,
  github: IconGitHub,
};

export function TeamSection() {
  return (
    <ScrollSection id="team" title="Built by operators, not just engineers" subtitle="Founded by a team with experience building and operating technology for regulated financial services. We understand compliance because we've lived it.">
      <div className="grid md:grid-cols-3 gap-6">
        {TEAM.map((member) => (
          <div
            key={member.name}
            className="border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden flex flex-col"
          >
            <img
              src={member.photo}
              alt={member.name}
              className="w-full aspect-[4/3] bg-[var(--color-bg-secondary)] object-cover"
              style={{ objectPosition: member.photoPosition }}
              loading="lazy"
            />
            <div className="p-6 md:p-8 flex flex-col flex-1">
              <div className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-2">
                {member.role}
              </div>
              <h3 className="text-lg font-medium mb-3">{member.name}</h3>
              <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed mb-10">
                {member.bio}
              </p>
              <div className="flex gap-3 pt-4 mt-auto border-t border-[var(--color-border)]">
                {Object.entries(member.links).map(([platform, url]) => {
                  const Icon = ICON_MAP[platform];
                  if (!Icon) return null;
                  return (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg)] transition-colors"
                      aria-label={`${member.name} on ${platform}`}
                    >
                      <Icon />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8">
        <Link
          to="/about"
          className="text-sm font-[family-name:var(--font-mono)] text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors"
        >
          Meet the full team →
        </Link>
      </div>
    </ScrollSection>
  );
}
